import React, { useState, useEffect } from "react";
import { Spin, message, Card, Table, Select, Button, Modal, Input } from "antd";
import { DownloadOutlined, LockOutlined } from "@ant-design/icons";
import { getMailFilesReport, getMailingLists, getMailFilesReportPeopleDetails } from "../../core/api/database";
import { useSelector } from "react-redux";
import DateRangeFilter, { formatDateRange } from './components/DateRangeFilter';
import MailingListFilter from './components/MailingListFilter';
import clientMapping from './constants/clientMapping';
import categoryOptions from './constants/categoryOptions';
import columns from './constants/tableColumns';
import { exportToExcel, getFormattedDate } from "../../utils/exportUtils";

const MFvsListTab = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [totalPeople, setTotalPeople] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(['all']);
  const [mailingListOptions, setMailingListOptions] = useState([]);
  const [selectedMailingLists, setSelectedMailingLists] = useState(['all']);
  const [loadingMailingLists, setLoadingMailingLists] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null
  });
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [downloadPassword, setDownloadPassword] = useState('');
  const [currentListCount, setCurrentListCount] = useState(null);
  
  // Get selectedClients from Redux store
  const selectedClients = useSelector((state) => state.selectedClients);

  useEffect(() => {
    fetchMailingLists();
    // Only fetch report data on initial load
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Automatically fetch data when selectedClients changes
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClients]);

  const fetchMailingLists = async () => {
    setLoadingMailingLists(true);
    try {
      const response = await getMailingLists();
      const lists = response.data;
      
      // Format lists for the Select component
      const formattedOptions = lists.map(list => ({
        label: list,
        value: list
      }));
      
      setMailingListOptions(formattedOptions);
      setLoadingMailingLists(false);
    } catch (error) {
      console.error("Error fetching mailing lists:", error);
      message.error("Failed to fetch mailing lists");
      setLoadingMailingLists(false);
    }
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const response = await getMailFilesReport(
        selectedClients, 
        selectedCategory, 
        selectedMailingLists,
        dateRange
      );
      
      // Add download handler to each row
      const dataWithDownloadHandler = response.data.data.map(item => ({
        ...item,
        onDownload: handleDownloadPeopleDetails
      }));
      
      setReportData(dataWithDownloadHandler);
      setTotalPeople(response.data.total_unique_people);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching mail files report data:", error);
      message.error("Failed to fetch mail files report data");
      setLoading(false);
    }
  };
  
  const handleCategoryChange = (value) => {
    // If 'all' is selected, deselect all other options
    if (value.includes('all') && !selectedCategory.includes('all')) {
      setSelectedCategory(['all']);
    } 
    // If 'all' is deselected and there are other selections, keep only those
    else if (!value.includes('all') && selectedCategory.includes('all')) {
      const newSelection = value.filter(item => item !== 'all');
      setSelectedCategory(newSelection.length > 0 ? newSelection : ['all']);
    }
    // Normal case: update selection
    else {
      setSelectedCategory(value);
    }
  };
  
  const handleMailingListsChange = (value) => {
    // If 'all' is selected, deselect all other options
    if (value.includes('all') && !selectedMailingLists.includes('all')) {
      setSelectedMailingLists(['all']);
    } 
    // If 'all' is deselected and there are other selections, keep only those
    else if (!value.includes('all') && selectedMailingLists.includes('all')) {
      const newSelection = value.filter(item => item !== 'all');
      setSelectedMailingLists(newSelection.length > 0 ? newSelection : ['all']);
    }
    // Normal case: update selection
    else {
      setSelectedMailingLists(value);
    }
  };
  
  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
  };
  
  const handleSelectAllMailingLists = () => {
    setSelectedMailingLists(['all']);
  };
  
  const handleDeselectAllMailingLists = () => {
    if (mailingListOptions.length > 0) {
      const allValues = mailingListOptions.map(option => option.value);
      setSelectedMailingLists(allValues);
    }
  };
  
  const handleApplyFilters = () => {
    fetchReportData();
  };
  
  const handleClearFilters = () => {
    setSelectedCategory(['all']);
    setSelectedMailingLists(['all']);
    setDateRange({
      startDate: null,
      endDate: null
    });
    // After clearing filters, apply them immediately
    setTimeout(() => fetchReportData(), 0);
  };

  const handleExportToExcel = () => {
    const fileName = `Mail_Files_Report_${getFormattedDate()}`;
    const data = reportData.map((row) => ({
      ...row,
      lists_count: row.lists_count.toString(),
    }));
    exportToExcel(data, fileName, columns.filter(col => col.key !== 'download'));
  };
  
  // Show password modal before downloading
  const showPasswordModal = (listCount) => {
    setCurrentListCount(listCount);
    setPasswordModalVisible(true);
    setDownloadPassword('');
  };

  // Handle password modal cancel
  const handlePasswordModalCancel = () => {
    setPasswordModalVisible(false);
    setDownloadPassword('');
    setCurrentListCount(null);
  };

  // Handle password input change
  const handlePasswordChange = (e) => {
    setDownloadPassword(e.target.value);
  };

  // Handle password submit
  const handlePasswordSubmit = () => {
    if (!downloadPassword.trim()) {
      message.error('Please enter the download password');
      return;
    }
    
    setPasswordModalVisible(false);
    handleDownloadPeopleDetails(currentListCount, downloadPassword);
  };
  
  // Download people details for a specific list count
  const handleDownloadPeopleDetails = async (listCount, password) => {
    setDownloadLoading(true);
    try {
      const response = await getMailFilesReportPeopleDetails(
        listCount,
        selectedClients, 
        selectedCategory, 
        selectedMailingLists,
        dateRange,
        password
      );
      
      if (response.data.data.length === 0) {
        message.info("No people data available for this list count");
        setDownloadLoading(false);
        return;
      }
      
      // Format the data for Excel export
      const peopleData = response.data.data.map(person => ({
        'Person ID': person.person_id,
        'Full Name': person.full_name || '',
        'Email': person.email || '',
        'Phone': person.phone || '',
        'Address': person.address || '',
        'City': person.city || '',
        'State': person.state || '',
        'Zip': person.zip || '',
        'Country': person.country || '',
        'Mailing Lists': person.mailing_lists ? person.mailing_lists.join(', ') : ''
      }));
      
      // Create Excel file name
      const fileName = `People_In_${listCount}_Lists_${getFormattedDate()}`;
      
      // Define columns for the Excel file
      const peopleColumns = [
        { title: 'Person ID', dataIndex: 'Person ID' },
        { title: 'Full Name', dataIndex: 'Full Name' },
        { title: 'Email', dataIndex: 'Email' },
        { title: 'Phone', dataIndex: 'Phone' },
        { title: 'Address', dataIndex: 'Address' },
        { title: 'City', dataIndex: 'City' },
        { title: 'State', dataIndex: 'State' },
        { title: 'Zip', dataIndex: 'Zip' },
        { title: 'Country', dataIndex: 'Country' },
        { title: 'Mailing Lists', dataIndex: 'Mailing Lists' }
      ];
      
      // Export to Excel
      exportToExcel(peopleData, fileName, peopleColumns);
      
      message.success(`Downloaded details for people in ${listCount} lists`);
    } catch (error) {
      console.error("Error downloading people details:", error);
      if (error.response && error.response.status === 403) {
        message.error('Invalid password. Please try again with the correct password.');
      } else {
        message.error("Failed to download people details");
      }
      setDownloadLoading(false);
    }
  };

  // Get company names for display
  const getSelectedCompanyNames = () => {
    if (!selectedClients || selectedClients.length === 0) {
      return 'All Companies';
    }
    
    if (selectedClients.includes('all')) {
      return 'All Companies';
    }
    
    return selectedClients
      .map(id => clientMapping[id] || '')
      .filter(name => name)
      .join(', ');
  };

  return (
    <div className="mf-vs-timeline-tab">
      <Card className="filter-card mb-4">
        <div className="row align-items-center">
          <div className="col-md-3 mb-3 mb-md-0">
            <label className="form-label">Category</label>
            <Select
              mode="multiple"
              value={selectedCategory}
              onChange={handleCategoryChange}
              options={categoryOptions}
              className="w-100"
              placeholder="Select Category"
              maxTagCount="responsive"
            />
          </div>
          
          <div className="col-md-4 mb-3 mb-md-0">
            <label className="form-label">Mailing Lists</label>
            <MailingListFilter 
              mailingListOptions={mailingListOptions}
              selectedMailingLists={selectedMailingLists}
              onMailingListsChange={handleMailingListsChange}
              loadingMailingLists={loadingMailingLists}
              onSelectAll={handleSelectAllMailingLists}
              onDeselectAll={handleDeselectAllMailingLists}
            />
          </div>
          
         
          
        
        </div>
        <div className=" " >
            <label className="form-label">Date Range</label>
            <DateRangeFilter 
              dateRange={dateRange}
              onChange={handleDateRangeChange}
            />
          </div>
        <div className="col-md-2 d-flex align-items-end">
            <div className="d-flex gap-2 mt-md-4">
              <Button 
                type="primary" 
                onClick={handleApplyFilters}
              >
                Apply Filters
              </Button>
              <Button 
                onClick={handleClearFilters}
              >
                Clear Filters
              </Button>
            </div>
          </div>
      </Card>
      
 
        
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Total Unique People: {totalPeople}</h5>
          <Button 
            type="primary" 
            icon={<DownloadOutlined />} 
            onClick={handleExportToExcel}
            disabled={reportData.length === 0}
          >
            Export to Excel
          </Button>
        </div>
 
      
      <Spin spinning={loading}>
        <Table 
          dataSource={reportData} 
          columns={[
            ...columns.filter(col => col.key !== 'download'),
            {
              title: 'Download',
              key: 'download',
              render: (_, record) => (
                <Button
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={() => showPasswordModal(record.list_count_value)}
                  loading={downloadLoading && currentListCount === record.list_count_value}
                />
              )
            }
          ]} 
          rowKey={(record) => record.lists_count}
          pagination={{ 
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
      </Spin>
      
      {/* Password Modal */}
      <Modal
        title="Password Required"
        open={passwordModalVisible}
        onCancel={handlePasswordModalCancel}
        footer={[
          <Button key="cancel" onClick={handlePasswordModalCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handlePasswordSubmit}>
            Download
          </Button>,
        ]}
      >
        <p>Please enter the password to download customer data:</p>
        <Input.Password
          prefix={<LockOutlined />}
          value={downloadPassword}
          onChange={handlePasswordChange}
          placeholder="Enter password"
          onPressEnter={handlePasswordSubmit}
          autoFocus
        />
      </Modal>
    </div>
  );
};

export default MFvsListTab;
